import React, { useMemo, useState } from 'react';
import { Box, Stack, Typography, Skeleton, IconButton, Tooltip, Button } from '@mui/material';
import { ContentCopy, EditOutlined } from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import type { AdmissionApplication, AdmissionCycle, IdNamePair } from '../../../types/admissions';
import { activeFormSections, fieldDisplayValue, isCnicFieldKey, isPhoneField, withoutPhoto } from '../form/admissionFormFields';
import { applicationFieldValue } from '../form/applicationFieldValues';
import { useApplicantGuardians } from '../form/useApplicantGuardians';
import EditApplicationDialog from '../dialogs/EditApplicationDialog';
import { InfoGrid, SectionCard } from '../../common/SectionCard';
import { checkAge } from '../../../utils/age';

interface Props {
  app: AdmissionApplication;
  cycle: AdmissionCycle;
  /** Show "Edit details" (staff who may correct answers). */
  canEdit?: boolean;
  /** After a correction is saved. */
  onChanged?: () => void | Promise<void>;
}

/**
 * Every answer on the application, grouped exactly as the cycle's form is
 * (same sections, same order, same labels), whether it was entered online,
 * at the front desk or by bulk upload. Answers are resolved from wherever
 * they were stored — the application, the family's Guardian records, or
 * custom-field data — by the same resolver the printed application uses.
 */
const CandidateDetailsTab: React.FC<Props> = ({ app, cycle, canEdit, onChanged }) => {
  const [editing, setEditing] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const guardians = useApplicantGuardians(app);

  const classOptions = useMemo(
    () => (cycle.allowedClassIds || []).filter((c): c is IdNamePair => typeof c === 'object'),
    [cycle.allowedClassIds],
  );
  // The photo is shown in the drawer's header, not as an answer.
  const sections = useMemo(() => withoutPhoto(activeFormSections(cycle.formTemplate?.sections)), [cycle.formTemplate?.sections]);

  // Age on the class's cut-off date, flagged when it misses the limit.
  const age = useMemo(
    () => checkAge(cycle.eligibility, typeof app.classId === 'object' ? app.classId?._id : app.classId, app.dob),
    [cycle.eligibility, app.classId, app.dob],
  );

  if (guardians === null) {
    return (
      <Stack spacing={2}>
        {[0, 1, 2].map(i => <Skeleton key={i} variant="rounded" height={120} />)}
      </Stack>
    );
  }

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      enqueueSnackbar('Copied', { variant: 'success' });
    } catch {
      enqueueSnackbar('Could not copy — select the text instead', { variant: 'warning' });
    }
  };

  return (
    <Stack spacing={2}>
      {canEdit && (
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: -0.5 }}>
          <Button size="small" startIcon={<EditOutlined fontSize="small" />} onClick={() => setEditing(true)} sx={{ textTransform: 'none' }}>
            Edit details
          </Button>
        </Stack>
      )}
      {editing && (
        <EditApplicationDialog open app={app} cycle={cycle} onClose={() => setEditing(false)} onSaved={() => onChanged?.()} />
      )}
      {sections.map(section => (
        <SectionCard key={section.key} title={section.title}>
          <InfoGrid>
            {section.fields.map(field => {
              const text = fieldDisplayValue(field, applicationFieldValue(field.key, { app, guardians }), classOptions);
              const copyable = !!text && (isCnicFieldKey(field.key) || isPhoneField(field) || field.type === 'email');
              return (
                <Box
                  key={field.key}
                  sx={{ minWidth: 0, gridColumn: field.type === 'textarea' ? { sm: 'span 2' } : undefined, '&:hover .copy': { opacity: 1 } }}
                >
                  <Typography variant="caption" color="text.secondary" display="block">{field.label}</Typography>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography
                      variant="body2"
                      fontWeight={text ? 500 : 400}
                      color={text ? 'text.primary' : 'text.disabled'}
                      sx={{ wordBreak: 'break-word', fontFamily: text && isCnicFieldKey(field.key) ? 'monospace' : undefined }}
                    >
                      {text || 'Not provided'}
                    </Typography>
                    {copyable && (
                      <Tooltip title="Copy">
                        <IconButton className="copy" size="small" onClick={() => copy(text)} sx={{ opacity: 0, transition: 'opacity 120ms', p: 0.25 }}>
                          <ContentCopy sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                  {field.key === 'dob' && age && (
                    <Typography variant="caption" color={age.fits ? 'text.secondary' : 'warning.dark'} display="block" sx={{ mt: 0.25 }}>
                      {age.age} on {age.on}{age.fits ? '' : ` · outside ${age.limit}`}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </InfoGrid>
        </SectionCard>
      ))}
    </Stack>
  );
};

export default CandidateDetailsTab;
